import * as Ariakit from '@ariakit/react'
import { useLayoutEffect, useRef } from 'react'

import { Carat, RotateClockwise } from '@syconium/little-miss-figgy'

import { PlacementId } from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'

import {
  PlacementCopy,
  placementName,
  placementSpokenLabel,
  priceSuffix,
  statusSuffix,
  withPlacementName,
} from './placementLabels'
import {
  CaratSlot,
  DisclosureToggle,
  PlacementCardBody,
  PlacementCardHeader,
  PlacementCardShell,
  PlacementCheckbox,
  PlacementHeaderText,
  PlacementLabel,
  PlacementStatus,
} from './styles'

export type PlacementDisclosureProps = {
  placementId: PlacementId
  priceLabel?: string
  statusLabel?: string
  isSelected: boolean
  isExpanded: boolean
  disabled?: boolean
  invalid?: boolean
  copy: PlacementCopy
  onToggleSelected(placementId: PlacementId): void
  onToggleExpanded(placementId: PlacementId): void
  /** The form for this placement. Rendered only while the card is open. */
  renderForm?(): React.ReactNode
}

/**
 * One placement in a multi-select picker, with its own form inside.
 */
export const PlacementDisclosure: React.FC<PlacementDisclosureProps> = ({
  placementId,
  priceLabel,
  statusLabel,
  isSelected,
  isExpanded,
  disabled = false,
  invalid = false,
  copy,
  onToggleSelected,
  onToggleExpanded,
  renderForm,
}) => {
  const toggleRef = useRef<HTMLButtonElement>(null)
  const name = placementName(placementId)
  const isOpen = isExpanded && !disabled

  const bodyRef = useRef<HTMLDivElement>(null)
  // Collapsing hides the form, so focus inside it (e.g. on Apply) moves to the card's toggle.
  // A layout effect, because after paint the browser has already dropped focus to the body.
  const wasOpen = useRef(isOpen)
  useLayoutEffect(() => {
    const collapsed = wasOpen.current && !isOpen
    wasOpen.current = isOpen
    if (collapsed && bodyRef.current?.contains(document.activeElement)) toggleRef.current?.focus()
  }, [isOpen])

  const spokenLabel = placementSpokenLabel(placementId, priceLabel, statusLabel)

  const headerText = (
    <PlacementHeaderText>
      <PlacementLabel $disabled={disabled}>
        {name}
        {priceSuffix(priceLabel)}
      </PlacementLabel>
      <PlacementStatus>{statusSuffix(statusLabel)}</PlacementStatus>
    </PlacementHeaderText>
  )

  return (
    <Ariakit.DisclosureProvider open={isOpen} setOpen={() => onToggleExpanded(placementId)}>
      <PlacementCardShell
        $selected={(isSelected && !disabled) || isOpen}
        $disabled={disabled}
        $invalid={invalid}
      >
        <PlacementCardHeader>
          <PlacementCheckbox
            aria-label={spokenLabel}
            checked={isSelected}
            disabled={disabled}
            onChange={() => onToggleSelected(placementId)}
            type='checkbox'
            value={placementId}
          />

          {disabled ? (
            headerText
          ) : (
            <Ariakit.Disclosure
              render={
                <DisclosureToggle
                  ref={toggleRef}
                  type='button'
                  aria-label={withPlacementName(
                    isOpen ? copy.collapsePlacement : copy.expandPlacement,
                    placementId
                  )}
                />
              }
            >
              {headerText}
              {(isOpen || isSelected) && (
                <CaratSlot>
                  <Carat
                    height='10'
                    width='10'
                    rotateClockwise={
                      isOpen ? RotateClockwise.ThreeQuarters : RotateClockwise.Quarter
                    }
                  />
                </CaratSlot>
              )}
            </Ariakit.Disclosure>
          )}
        </PlacementCardHeader>

        <Ariakit.DisclosureContent ref={bodyRef} render={<PlacementCardBody />}>
          {renderForm?.()}
        </Ariakit.DisclosureContent>
      </PlacementCardShell>
    </Ariakit.DisclosureProvider>
  )
}
